import React from "react";
import { CalendarClock, Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR, shortDate } from "@/lib/format";

/** Which chase-list a studio belongs to. */
export const tenantBucket = (tn) => {
  const sub = tn.subscription || {};
  if (sub.status === "lifetime") return "lifetime";
  const end = sub.current_period_end ? new Date(sub.current_period_end) : null;
  if (!end) return "active";
  const days = (end - new Date()) / 86400000;
  if (days < 0) return "locked";
  if (days <= 7) return "expiring";
  return "active";
};

/** Shows an operator lockout instead of a misleading "trialing" label. */
export const tenantStatusLabel = (sub, t) => {
  if (!sub) return "—";
  if (sub.status === "lifetime") return t("sub_status_lifetime");
  const end = sub.current_period_end ? new Date(sub.current_period_end) : null;
  if (end && end < new Date()) return t("locked_out");
  return t(`sub_status_${sub.status}`);
};

const FILTERS = ["all", "locked", "expiring", "lifetime"];

/** Studio list with the chase-list filter chips. */
export const TenantList = ({ tenants, counts, filter, onFilter, onOverride, onDelete }) => {
  const { t } = useI18n();

  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7" data-testid="tenants-list">
      <p className="eyebrow mb-5">{t("tenants")}</p>
      <p className="text-[11px] text-[#707070] mb-5">
        Subscription metadata only — studio projects and client data stay private to each studio.
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="tenant-filters">
        {FILTERS.map((key) => (
          <button key={key} data-testid={`tenant-filter-${key}`} onClick={() => onFilter(key)}
            className={`font-mono-accent text-[10px] tracking-[0.16em] uppercase border px-3 py-2 transition-colors ${
              filter === key ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A8A8] hover:border-[#D4AF37]"
            }`}>
            {t(`filter_${key}`)} <span className="ml-2 text-[#707070]">{counts[key]}</span>
          </button>
        ))}
      </div>

      {tenants.length === 0 && (
        <p className="text-sm text-[#707070]" data-testid="no-tenants-in-filter">{t("no_search_results")}</p>
      )}

      {tenants.map((tn) => (
        <div key={tn.tenant_id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-[#1E1E1E]">
          <div>
            <p className="text-sm">{tn.name}</p>
            <p className="text-[11px] text-[#707070]">
              since {shortDate(tn.created_at)}
              {tn.renewal_nudged_at && (
                <span className="ml-2 text-[#D4AF37]" data-testid={`tenant-nudged-${tn.tenant_id}`}>
                  · {t("renewal_nudged")} {shortDate(tn.renewal_nudged_at)}
                </span>
              )}
            </p>
          </div>
          <span className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#A8A8A8]"
            data-testid={`tenant-status-${tn.tenant_id}`}>
            {tenantStatusLabel(tn.subscription, t)}
          </span>
          <span className="font-mono-accent text-xs text-[#D4AF37]">{IDR(tn.paid_total)}</span>
          <span className="flex items-center gap-3">
            <button data-testid={`override-sub-${tn.tenant_id}`} onClick={() => onOverride(tn.tenant_id)}
              className="inline-flex items-center gap-2 border border-[#2A2A2A] px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase text-[#A8A8A8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <CalendarClock size={13} /> {t("subscription_override")}
            </button>
            <button data-testid={`delete-tenant-${tn.tenant_id}`} aria-label={t("delete_studio")}
              title={t("delete_studio_warning")} onClick={() => onDelete(tn)}
              className="text-[#5A5A5A] hover:text-[#8B0000]"><Trash2 size={14} /></button>
          </span>
        </div>
      ))}
    </div>
  );
};
